import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useExpertiseDetail } from './hooks/useExpertiseDetail';
import { ExpertiseDetailPageProps } from './types/expertise-detail.types';
import { ExpertiseHeader } from './components/ExpertiseHeader';
import { ExpertiseTimeline } from './components/ExpertiseTimeline';
import { ExpertiseTabs } from './components/ExpertiseTabs';
import { Button } from '../../../../ui/components/atoms/Button';
import { Loading } from '@/ui/components/atoms/Loading';
// import './styles/ExpertiseDetail.module.css';

export const ExpertiseDetailPage: React.FC<ExpertiseDetailPageProps> = ({
  dossierId,
  backUrl = '/dashboard',
  // variant = 'full',
  showActions = true,
  onBack,
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  
  // Fetch expertise data
  const {
    expertise,
    affaireData,
    isLoading,
    error,
    canEdit,
    canDelete,
  } = useExpertiseDetail(dossierId);

  // Handle back navigation
  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(backUrl);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="expertise-detail">
        <Loading 
          size="large" 
          text="Chargement de l'expertise..." 
        />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="expertise-detail-error">
        <div className="error-message">
          <h2>Erreur</h2>
          <p>{error}</p>
          <Button onClick={handleBack} variant="secondary">
            Retour
          </Button>
        </div>
      </div>
    );
  }

  // No data state
  if (!expertise) {
    return (
      <div className="expertise-detail-empty">
        <div className="empty-message">
          <h2>Aucune expertise trouvée</h2>
          <p>L'expertise demandée n'existe pas ou n'est pas accessible.</p>
          <Button onClick={handleBack} variant="secondary">
            Retour
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="expertise-detail">
      {/* Header Section */}
      <ExpertiseHeader
        expertise={expertise}
        onBack={handleBack}
        backUrl={backUrl}
      />

      {/* Timeline Section */}
      <ExpertiseTimeline
        currentStep={expertise.currentStep}
        completionPercentage={expertise.completionPercentage}
        status={expertise.status}
        createdDate={expertise.createdDate}
        scheduledDate={expertise.scheduledDate}
        statusAffaire={(affaireData.biens && affaireData.biens[0]?.status_affaire) ?? undefined}
      />

      {/* Tabs Section */}
      <ExpertiseTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        expertise={expertise}
        affaireData={affaireData}
      />

      {/* Actions Section */}
      {showActions && (canEdit || canDelete) && (
        <div className="expertise-detail-actions">
          {canEdit && (
            <Button variant="secondary" size="medium">
              Modifier
            </Button>
          )}
          {canDelete && (
            <Button variant="danger" size="medium">
              Supprimer
            </Button>
          )}
        </div>
      )}

      {/* Modern Professional Styling */}
      <style>{`
        /* Main Container - Clean & Spacious */
        .expertise-detail {
          margin: 0;
          padding: 24px;
          background: linear-gradient(180deg, #FAFBFC 0%, #F5F7FA 100%);
          min-height: 100vh;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Inter', sans-serif;
          animation: fadeIn 0.4s ease;
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* Actions Section - Floating Action Bar */
        .expertise-detail-actions {
          position: sticky;
          bottom: 24px;
          z-index: 100;
          display: flex;
          gap: 12px;
          justify-content: center;
          margin-top: 40px;
          padding: 16px;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          border-radius: 16px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
          max-width: fit-content;
          margin-left: auto;
          margin-right: auto;
        }
        
        /* Error & Empty States - Clean Card Design */
        .expertise-detail-error,
        .expertise-detail-empty {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
          background: linear-gradient(180deg, #FAFBFC 0%, #F5F7FA 100%);
          padding: 24px;
        }
        
        .error-message,
        .empty-message {
          text-align: center;
          padding: 48px;
          background: white;
          border-radius: 24px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
          max-width: 500px;
          width: 100%;
        }
        
        .error-message::before,
        .empty-message::before {
          content: '⚠️';
          display: block;
          font-size: 48px;
          margin-bottom: 24px;
        }
        
        .empty-message::before {
          content: '📋';
        }
        
        .error-message h2,
        .empty-message h2 {
          color: #002035;
          font-size: 24px;
          font-weight: 700;
          margin: 0 0 12px 0;
        }
        
        .error-message p,
        .empty-message p {
          color: #64748B;
          font-size: 16px;
          line-height: 1.6;
          margin: 0 0 32px 0;
        }
        
        /* Back button uses the Button component styles */
        
        /* Responsive Design */
        @media (max-width: 1024px) {
          .expertise-detail {
            padding: 20px;
          }
        }
        
        @media (max-width: 768px) {
          .expertise-detail {
            padding: 16px;
          }
          
          .expertise-detail-actions {
            position: fixed;
            bottom: 16px;
            left: 16px;
            right: 16px;
            margin: 0;
            width: auto;
            padding: 12px;
            flex-direction: column;
          }
          
          .expertise-detail-actions .chatapp-button {
            width: 100%;
          }
          
          .error-message,
          .empty-message {
            padding: 32px 24px;
            border-radius: 20px;
          }
        }
        
        @media (max-width: 480px) {
          .expertise-detail {
            padding: 12px;
          }
          
          .expertise-detail-actions {
            bottom: 12px;
            left: 12px;
            right: 12px;
          }
        }
      `}</style>
    </div>
  );
};